import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How you can translate any random D3 example to React",
  "description": "",
  "published": "2017-08-31T00:00:00.000Z",
  "redirect_from": ["/blog/translate-random-d3-example-react/swizec/7765"],
  "categories": "d3js, Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "pzMTVChKvjo",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`You and I both know that when it's time to code some D3, the easiest approach is to find an example that does something similar, copy its code, and tweak it a bit.`}</p>
    <p>{`But a lot of those examples are in old versions of D3, and what if you're using React or something and can't just plop code into your project like nobody's business? Here's what you do:`}</p>
    <ol>
      <li parentName="ol">{`Find the example`}</li>
      <li parentName="ol">{`Split it up into styling, data calc, and rendering`}</li>
      <li parentName="ol">{`Put those in the appropriate place for your environment`}</li>
    </ol>
    <p>{`Let's do that for this example of `}<a parentName="p" {...{
        "href": "https://bl.ocks.org/mbostock/4657115"
      }}>{`113th Congressional District`}</a>{` from March 2016.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/08/2017-08-31-at-3.17-PM.png",
        "alt": null
      }}></img></p>
    <p>{`The code starts with the usual HTML declarations, loading scripts, and a block of `}<inlineCode parentName="p">{`<style>`}</inlineCode>{` stuff. Assuming we started our React app with `}<inlineCode parentName="p">{`create-react-app`}</inlineCode>{`, we copy all the style info into `}<inlineCode parentName="p">{`App.css`}</inlineCode>{`. That way we never have to worry about it again.`}</p>
    <p>{`We need styling because SVG elements are invisible by default.`}</p>
    <h2 {...{
      "id": "setup-a-component"
    }}>{`Setup a component`}</h2>
    <p>{`To make our lives easy, we're going to use the D3 blackbox approach to integration React and D3. By doing it this way, we can reuse all of the example code almost without change, and we lose most of React's benefits. Great for slapping code together quickly, but I'd suggest a more structured approach for anything serious.`}</p>
    <p>{`We need a component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/CongressionalDistricts.js

import React, { Component } from 'react';
import * as topojson from 'topojson';
import * as d3 from 'd3';

class CongressionalDistricts extends Component {
    state = {
        usData: null,
        usCongress: null
    }

    componentWillMount() {
        // load data
    }

    componentDidUpdate() {
        // render example D3
    }

    render() {
        const { usData, usCongress } = this.state;

        if (!usData || !usCongress) {
            return null;
        }

        return <g ref="anchor">;
    }
}

export default CongressionalDistricts;
</g>
`}</code></pre>
    <p>{`Our `}<inlineCode parentName="p">{`CongressionalDistricts`}</inlineCode>{` component has three parts. We're going to load data in `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{`, do all of our significant rendering in `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`, and render an anchor element in `}<inlineCode parentName="p">{`render`}</inlineCode>{`.`}</p>
    <p>{`This approach hands control of the DOM over to D3, which is why React can't help us. Once we're inside that anchor element, it's all D3 with this approach.`}</p>
    <h2 {...{
      "id": "copypasta-the-code"
    }}>{`Copypasta the code`}</h2>
    <p>{`Next in our example, we find the setup code.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var width = 960,
  height = 600;

var projection = d3.geo
  .albersUsa()
  .scale(1280)
  .translate([width / 2, height / 2]);

var path = d3.geo.path().projection(projection);

var svg = d3
  .select("body")
  .append("svg")
  .attr("width", width)
  .attr("height", height);
`}</code></pre>
    <p>{`This code is great, but has some problems.`}</p>
    <p>{`It's still using `}<inlineCode parentName="p">{`var`}</inlineCode>{` declarations, which are not encouraged in modern ES6. It also uses old D3 APIs which are going to fail when you run `}<inlineCode parentName="p">{`npm install d3`}</inlineCode>{` because you'll get the new D3v4 version.`}</p>
    <p>{`On a practical level, it's setting up a new geographical projection, path generator, and creating an `}<inlineCode parentName="p">{`svg`}</inlineCode>{` element. We can throw away the `}<inlineCode parentName="p">{`svg`}</inlineCode>{` element creation because we're wrapping our component in svg anyway.`}</p>
    <p>{`So we copy the projection and path setup into `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{` like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/CongressionalDistricts.js
    componentDidUpdate() {
        const svg = d3.select(this.refs.anchor),
              { width, height } = this.props;

        const projection = d3.geoAlbers()
                             .scale(1280)
                             .translate([width / 2, height / 2]);

        const path = d3.geoPath(projection);

        const us = this.state.usData,
              congress = this.state.usCongress;
`}</code></pre>
    <p>{`We select our anchor element and name it `}<inlineCode parentName="p">{`svg`}</inlineCode>{`. That way we won't have to change any of the other code.`}</p>
    <p><inlineCode parentName="p">{`d3.geo.albersUsa`}</inlineCode>{` becomes `}<inlineCode parentName="p">{`d3.geoAlbers`}</inlineCode>{` and `}<inlineCode parentName="p">{`d3.geo.path`}</inlineCode>{` becomes `}<inlineCode parentName="p">{`d3.geoPath`}</inlineCode>{`. Same thing, simpler names. That's the general principle behind the D3v3 to D3v4 transition.`}</p>
    <p>{`The example code assumes data is global, so we take it out of state and put it in variables. Again, so we don't have to change any of the code.`}</p>
    <p>{`Next, the example loads its data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`queue()
  .defer(d3.json, "/mbostock/raw/4090846/us.json")
  .defer(d3.json, "/mbostock/raw/4090846/us-congress-113.json")
  .await(ready);
`}</code></pre>
    <p>{`This goes in our `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` method. No sense fetching data before we know the component is getting mounted. And we definitely don't want to do it on every render :)`}</p>
    <p>{`Also, we change the URLs and download those files into our `}<inlineCode parentName="p">{`/public`}</inlineCode>{` folder.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/CongressionalDistricts.js
    componentWillMount() {
        d3.queue()
          .defer(d3.json, "us.json")
          .defer(d3.json, "us-congress-113.json")
          .await((error, usData, usCongress) => {
              this.setState({
                  usData,
                  usCongress
              });
          })
    }
`}</code></pre>
    <p>{`This loads both datasets and updates component state, which triggers a re-render. Notice that with D3v4, `}<inlineCode parentName="p">{`queue`}</inlineCode>{` is now an official part of D3. It helps us load multiple datasets sequentially.`}</p>
    <p>{`Now for the coup-de-grace: A massive copypasta that puts all the rendering code from our example into our React component. It's messy but it works.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/CongressionalDistricts.js

    componentDidUpdate() {
        const svg = d3.select(this.refs.anchor),
              { width, height } = this.props;

        const projection = d3.geoAlbers()
                             .scale(1280)
                             .translate([width / 2, height / 2]);

        const path = d3.geoPath(projection);

        const us = this.state.usData,
              congress = this.state.usCongress;

        // Pure copypasta starts here
        svg.append("defs").append("path")
           .attr("id", "land")
           .datum(topojson.feature(us, us.objects.land))
           .attr("d", path);

        svg.append("clipPath")
           .attr("id", "clip-land")
           .append("use")
           .attr("xlink:href", "#land");

        svg.append("g")
           .attr("class", "districts")
           .attr("clip-path", "url(#clip-land)")
           .selectAll("path")
           .data(topojson.feature(congress, congress.objects.districts).features)
           .enter().append("path")
           .attr("d", path)
           .append("title")
           .text(function(d) { return d.id; });

        svg.append("path")
           .attr("class", "district-boundaries")
           .datum(topojson.mesh(congress, congress.objects.districts, function(a, b) { return a !== b && (a.id / 1000 | 0) === (b.id / 1000 | 0); }))
           .attr("d", path);

        svg.append("path")
           .attr("class", "state-boundaries")
           .datum(topojson.mesh(us, us.objects.states, function(a, b) { return a !== b; }))
           .attr("d", path);
    }
`}</code></pre>
    <p>{`Copy all the code over, and you should get a map. It's great.`}</p>
    <h2 {...{
      "id": "make-it-better"
    }}>{`Make it better`}</h2>
    <p>{`Now here's what I'd do next. You see all those little `}<inlineCode parentName="p">{`svg.append`}</inlineCode>{` calls? I'd change them to individual components.`}</p>
    <p>{`That way you could have a render method that looks more like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<g>
  <usland>
    <usstateboundaries>
      <usdistrictboundaries></usdistrictboundaries>
    </usstateboundaries>
  </usland>
</g>
`}</code></pre>
    <p>{`I think that sort of code is easier to understand.`}</p>
    <p>{`You can play with this example on Codepen 👇`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/GvPexr/"
      }}>{`113th congressional districts D3 example moved into React`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      